import { Link } from 'react-router-dom'
import { BrandLogo } from '../shared/BrandLogo'
import './web-test-motion.css'
import './web-test-hero.css'
import './web-test-legal.css'
import { portalLinks } from './portal-links'
import { WebsiteThemeToggle } from './WebsiteThemeToggle'
import { RISK_LINKS } from './risk-copy'
import { SeoHead } from './seo/SeoHead'
import { defaultPageGraph } from './seo/jsonld'
import { getPageSeo } from './seo/pages'

export type LegalDocId = 'terms' | 'privacy' | 'cookies' | 'risk-disclosure'

const DOCS: Record<
  LegalDocId,
  {
    title: string
    updated: string
    sections: { heading: string; paragraphs: string[] }[]
  }
> = {
  terms: {
    title: 'Terms of Use',
    updated: '7 August 2026',
    sections: [
      {
        heading: '1. Overview',
        paragraphs: [
          'These Terms of Use govern access to and use of the Perfect Predict public website and related informational materials. By using this website, you agree to these terms.',
          'Perfect Predict provides structured prediction trading information and access pathways to a Client Portal. Trading services are subject to separate account opening, eligibility, and product terms presented during registration and within the Client Portal.',
        ],
      },
      {
        heading: '2. Informational nature of this website',
        paragraphs: [
          'Content on this website, including strategy illustrations, payout examples, and market descriptions, is provided for general information only. Illustrative figures are examples and are not guarantees of performance.',
          'Nothing on this website constitutes investment advice, a solicitation, or an offer to buy or sell any financial instrument in any jurisdiction where such activity would be unlawful.',
        ],
      },
      {
        heading: '3. Eligibility and account access',
        paragraphs: [
          'Access to live trading features requires successful registration, verification steps required by Perfect Predict, and acceptance of applicable Client Portal terms.',
          'You are responsible for maintaining the confidentiality of your login credentials and for activity under your account.',
        ],
      },
      {
        heading: '4. Acceptable use',
        paragraphs: [
          'You must not misuse the website, attempt unauthorized access to systems, interfere with service integrity, or use the website in a manner that violates applicable law.',
        ],
      },
      {
        heading: '5. Intellectual property',
        paragraphs: [
          'Website content, branding, layout, and related materials are owned by Perfect Predict or its licensors. You may not copy, modify, or redistribute them without prior written permission, except for personal, non-commercial viewing.',
        ],
      },
      {
        heading: '6. Limitation of liability',
        paragraphs: [
          'To the fullest extent permitted by law, Perfect Predict is not liable for losses arising from reliance on website information, service interruptions, or trading outcomes. Trading involves risk and can result in loss of capital.',
        ],
      },
      {
        heading: '7. Changes',
        paragraphs: [
          'We may update these Terms of Use from time to time. The updated date above reflects the current version. Continued use of the website after changes constitutes acceptance of the revised terms.',
        ],
      },
      {
        heading: '8. Contact',
        paragraphs: [
          'For questions about these terms, contact support through the Client Portal Support channel.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    updated: '7 August 2026',
    sections: [
      {
        heading: '1. Scope',
        paragraphs: [
          'This Privacy Policy explains how Perfect Predict handles personal information collected through the public website and, where applicable, during Client Portal registration and support interactions.',
        ],
      },
      {
        heading: '2. Information we collect',
        paragraphs: [
          'Depending on how you interact with Perfect Predict, we may collect identity and contact details (such as name and email), account and verification information, technical data (such as IP address, browser type, and device information), and communications you send to support.',
          'This public website does not display client account data. Account information is processed through the secure Perfect Predict Client Portal and related service infrastructure.',
        ],
      },
      {
        heading: '3. How we use information',
        paragraphs: [
          'We use personal information to operate and secure services, process account requests, communicate service updates, meet legal and compliance obligations, and improve product reliability.',
          'On the public website, optional analytics (PostHog) runs only after you accept analytics cookies. It is used for aggregate usage measurement on marketing pages and does not collect passwords, account balances, or form field contents.',
        ],
      },
      {
        heading: '4. Sharing',
        paragraphs: [
          'We may share information with service providers that support hosting, security, communications, payments, or infrastructure, and where required by law or regulatory request. We do not sell personal information.',
        ],
      },
      {
        heading: '5. Retention and security',
        paragraphs: [
          'We retain information only as long as needed for the purposes described or as required by law. We apply administrative and technical safeguards appropriate to the sensitivity of the data.',
        ],
      },
      {
        heading: '6. Your choices',
        paragraphs: [
          'Depending on applicable law, you may request access, correction, or deletion of certain personal information, or raise a privacy inquiry through Client Portal Support.',
        ],
      },
      {
        heading: '7. Updates',
        paragraphs: [
          'We may update this Privacy Policy periodically. The updated date above reflects the current version.',
        ],
      },
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    updated: '8 August 2026',
    sections: [
      {
        heading: '1. What cookies are',
        paragraphs: [
          'Cookies and similar technologies are small files or storage items used to operate websites, remember preferences, and understand how pages are used.',
        ],
      },
      {
        heading: '2. How Perfect Predict uses cookies',
        paragraphs: [
          'We use strictly necessary cookies and local storage required for site operation and security (for example display-theme preference).',
          'With your consent, we may also use optional analytics technologies provided by PostHog to understand aggregate public-website usage, such as which pages are viewed and which public calls-to-action are clicked. Analytics cookies and storage are not set for this purpose until you accept analytics in the cookie banner.',
          'Session replay on the public website, if enabled after consent, masks form inputs and does not record Client Portal, Admin, login, registration, funding, or trading screens.',
        ],
      },
      {
        heading: '3. Managing cookies',
        paragraphs: [
          'You can Accept analytics or Reject non-essential analytics using the cookie banner on this website. You can change your choice later from Cookie settings (#cookie-settings) or by clearing site data in your browser.',
          'You can also control cookies through your browser settings. Blocking strictly necessary storage may affect site functionality such as theme preference.',
        ],
      },
      {
        heading: '4. Updates',
        paragraphs: [
          'We may update this Cookie Policy as our practices or legal requirements change. The updated date above reflects the current version.',
        ],
      },
    ],
  },
  'risk-disclosure': {
    title: 'Risk Disclosure',
    updated: 'August 2026',
    sections: [
      {
        heading: '1. General Trading Risk',
        paragraphs: [
          'Trading financial instruments involves significant risk and may not be suitable for every person. Before using Perfect Predict, carefully consider your financial situation, experience and risk tolerance.',
        ],
      },
      {
        heading: '2. Risk of Loss',
        paragraphs: [
          'You may lose some or all of the capital allocated to trading. Losses can occur quickly. Do not trade with funds you cannot afford to lose.',
        ],
      },
      {
        heading: '3. Leveraged Trading Risk',
        paragraphs: [
          'Leverage can magnify both gains and losses. Small market movements may have a large impact on your account equity when leverage is used.',
        ],
      },
      {
        heading: '4. Market Volatility',
        paragraphs: [
          'Gold and Silver prices can change rapidly due to economic data, geopolitical events, liquidity conditions and other market factors. Volatility can widen spreads and increase the likelihood of stop or target levels being reached unexpectedly.',
        ],
      },
      {
        heading: '5. Execution and Liquidity Risk',
        paragraphs: [
          'Order execution may be affected by market volatility, liquidity, price gaps, slippage, latency, technical conditions and market availability. Requested prices are not always available and fills may differ from illustrative previews.',
        ],
      },
      {
        heading: '6. Strategy Parameters',
        paragraphs: [
          'SAFE, BALANCE and AGGRESSIVE are predefined strategy profiles that present structured target and stop parameters. These names describe risk/reward profiles. They do not indicate certainty, lower loss probability, or guaranteed success.',
          'Selecting any strategy does not remove market risk or the possibility of loss.',
        ],
      },
      {
        heading: '7. Illustrative Payout Examples',
        paragraphs: [
          'Payout, return, target and stop figures shown on this website are illustrative examples only. They help explain product structure and do not guarantee that a target will be reached or that any specific payout will be achieved.',
        ],
      },
      {
        heading: '8. No Guaranteed Outcomes',
        paragraphs: [
          'Perfect Predict does not guarantee profits, returns, target achievement or protection from loss.',
        ],
      },
      {
        heading: '9. No Investment Advice',
        paragraphs: [
          'Information on this website is general product information. It does not constitute personal investment, legal, tax or financial advice. You should seek independent advice if you are unsure whether Perfect Predict is suitable for you.',
        ],
      },
      {
        heading: '10. Technology and Platform Risk',
        paragraphs: [
          'Technical interruptions, connectivity problems, maintenance windows or third-party platform issues (including MetaTrader 5 infrastructure) may affect access, quoting or execution.',
        ],
      },
      {
        heading: '11. Funding and Withdrawal Risk',
        paragraphs: [
          'Deposit and withdrawal processing may depend on identity verification, supported payment providers, network conditions and compliance checks. Processing times can vary and are not guaranteed.',
        ],
      },
      {
        heading: '12. Client Responsibility',
        paragraphs: [
          'You are responsible for assessing whether Perfect Predict is suitable for you, understanding the product mechanics, reviewing applicable documentation and managing your own trading decisions.',
        ],
      },
      {
        heading: '13. Jurisdiction and Eligibility',
        paragraphs: [
          'Availability may vary by jurisdiction. Clients are responsible for confirming whether access to the service is permitted in their location.',
        ],
      },
      {
        heading: '14. Updates to This Disclosure',
        paragraphs: [
          'We may update this Risk Disclosure from time to time. The “Last updated” date reflects the current version. Continued use of the website after updates means you should review the revised disclosure.',
        ],
      },
      {
        heading: '15. Contact and Support',
        paragraphs: [
          'For questions about this disclosure or your account, contact Perfect Predict Support through the Client Portal: https://portal.perfectpredict.com/app/support',
        ],
      },
    ],
  },
}

const LEGAL_PATH: Record<LegalDocId, string> = {
  terms: '/terms',
  privacy: '/privacy',
  cookies: '/cookies',
  'risk-disclosure': '/risk-disclosure',
}

export function WebTestLegalPage({ doc }: { doc: LegalDocId }) {
  const content = DOCS[doc]
  const path = LEGAL_PATH[doc]
  const seo = getPageSeo(path)!

  return (
    <div className="wt-page wl-page">
      <SeoHead path={path} jsonLd={defaultPageGraph(seo)} />
      <header className="wl-header">
        <div className="wl-header__inner">
          <BrandLogo className="wt-logo" tone="light" to="/" />
          <nav className="wl-header__nav" aria-label="Legal">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/cookies">Cookies</Link>
            <Link to={RISK_LINKS.disclosure}>Risk Disclosure</Link>
          </nav>
          <div className="wl-header__actions">
            <WebsiteThemeToggle />
            <a className="wt-btn wt-btn--ghost" href={portalLinks.login}>
              Client Portal
            </a>
            <a className="wt-btn wt-btn--primary" href={portalLinks.register}>
              Open Live Account
            </a>
          </div>
        </div>
      </header>

      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">Legal</p>
          <h1 className="wl-title">{content.title}</h1>
          <p className="wl-updated">Last updated: {content.updated}</p>

          {content.sections.map((section) => (
            <section key={section.heading} className="wl-section">
              <h2>{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p.slice(0, 48)}>{p}</p>
              ))}
            </section>
          ))}

          <p className="wl-back">
            <Link to="/">← Back to Perfect Predict</Link>
            {doc === 'cookies' ? (
              <>
                {' · '}
                <a href="#cookie-settings">Cookie settings</a>
              </>
            ) : null}
          </p>
        </div>
      </main>
    </div>
  )
}
